import { useState } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Download, ChevronDown, ChevronUp } from 'lucide-react';
import type { BulkOperationResultDto } from '../../types/cutover';
import './BulkOperationResultPanel.css';

interface BulkOperationResultPanelProps {
  result: BulkOperationResultDto;
  title?: string;
}

const ACTION_STYLES: Record<string, { className: string }> = {
  Created: { className: 'badge-success' },
  Updated: { className: 'badge-success' },
  Deactivated: { className: 'badge-success' },
  Skipped: { className: 'badge-warning' },
  Conflict: { className: 'badge-conflict' },
  Failed: { className: 'badge-danger' },
  // SLV2-3846: a priced fee the sheet said "No" to was kept; a person decides.
  Review: { className: 'badge-conflict' },
};

const INITIAL_VISIBLE = 20;

export default function BulkOperationResultPanel({ result, title }: Readonly<BulkOperationResultPanelProps>) {
  const [expanded, setExpanded] = useState(false);
  // SLV2-3846: rows a person must decide on are counted and listed first, so they are never
  // hidden below the first INITIAL_VISIBLE rows of a large run.
  const reviewItems = result.items.filter((item) => item.action === 'Review');
  const orderedItems = [...reviewItems, ...result.items.filter((item) => item.action !== 'Review')];
  const visibleItems = expanded ? orderedItems : orderedItems.slice(0, INITIAL_VISIBLE);
  const hasMore = orderedItems.length > INITIAL_VISIBLE;

  const handleExportCsv = () => {
    if (result.items.length === 0) return;

    const headers = ['Item Type', 'Item Name', 'Action', 'Details', 'Item ID'];
    const rows = result.items.map((item) => [
      item.itemType,
      item.itemName,
      item.action,
      item.details ?? '',
      item.itemId?.toString() ?? '',
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bulk-operation-${result.operationLogId}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="borp">
      {/* Header */}
      <div className="borp-header">
        <h3 className="borp-title">{title ?? 'Operation Results'}</h3>
        {result.isDryRun && (
          <span className="borp-dry-run-badge">Dry Run</span>
        )}
      </div>

      {/* Summary Bar */}
      <div className="borp-summary">
        <div className="borp-stat borp-stat-success">
          <CheckCircle size={16} />
          <span>{result.successCount} succeeded</span>
        </div>
        <div className="borp-stat borp-stat-failure">
          <XCircle size={16} />
          <span>{result.failureCount} failed</span>
        </div>
        <div className="borp-stat borp-stat-skipped">
          <AlertTriangle size={16} />
          <span>{result.skippedCount} skipped</span>
        </div>
        {reviewItems.length > 0 && (
          <div className="borp-stat borp-stat-review">
            <AlertTriangle size={16} />
            <span>{reviewItems.length} need review</span>
          </div>
        )}
        <div className="borp-stat borp-stat-total">
          <span>{result.totalRecords} total</span>
        </div>
        {result.durationMs > 0 && (
          <div className="borp-duration">
            {result.durationMs < 1000
              ? `${result.durationMs}ms`
              : `${(result.durationMs / 1000).toFixed(1)}s`}
          </div>
        )}
      </div>

      {/* Item Details */}
      {result.items.length > 0 && (
        <div className="borp-items">
          <div className="borp-items-header">
            <span className="borp-items-count">{result.items.length} items</span>
            <button type="button" className="borp-export-btn" onClick={handleExportCsv}>
              <Download size={14} />
              Export CSV
            </button>
          </div>
          <table className="borp-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Name</th>
                <th>Action</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((item, idx) => {
                const style = ACTION_STYLES[item.action] ?? { className: 'badge-default' };
                return (
                  <tr key={`${item.itemType}-${item.itemName}-${idx}`}>
                    <td className="borp-cell-type">{item.itemType}</td>
                    <td className="borp-cell-name">{item.itemName}</td>
                    <td>
                      <span className={`borp-action-badge ${style.className}`}>
                        {item.action}
                      </span>
                    </td>
                    <td className="borp-cell-details">{item.details ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {hasMore && (
            <button
              type="button"
              className="borp-show-more"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? (
                <>
                  <ChevronUp size={14} />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown size={14} />
                  Show all {result.items.length} items
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}